import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
  formatDate, 
  formatNumber, 
  getStatusBadgeClass, 
  capitalize, 
  getPlanColor
} from '../utils/helpers';
import {
  ArrowLeft,
  User,
  Mail,
  Calendar,
  Shield,
  CreditCard,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Activity,
  Package,
  Clock,
  FileText,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Edit
} from 'lucide-react';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/users/${userId}`);
      setUser(response.data.user);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Fetch user detail error:', error);
      toast.error('Failed to load user details');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (updatedData) => {
    try {
      await api.put(`/admin/users/${userId}`, updatedData);
      toast.success('User updated successfully');
      fetchUserDetail();
      setShowEditModal(false);
    } catch (error) {
      console.error('Update user error:', error);
      toast.error('Failed to update user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const subscription = user.subscription || {};
  const monthlyRecurringRevenue = subscription.billingCycle === 'monthly' 
    ? (user.plan === 'pro' ? 5 : user.plan === 'premium' ? 10 : user.plan === 'basic' ? 3 : 0)
    : 0;
  const yearlyRecurringRevenue = subscription.billingCycle === 'yearly'
    ? (user.plan === 'pro' ? 25 : user.plan === 'premium' ? 50 : user.plan === 'basic' ? 15 : 0)
    : 0;
  const totalRecurringRevenue = monthlyRecurringRevenue + yearlyRecurringRevenue;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/users')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
            <p className="text-gray-600 mt-1">{user.email}</p>
          </div>
        </div>
        {/* <button
          onClick={() => setShowEditModal(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Edit className="w-4 h-4" />
          <span>Edit User</span>
        </button> */}
      </div>

      {/* User Info Card */}
      <div className="card">
        <div className="card-body">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">User Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Name</span>
              </div>
              <p className="text-gray-900 font-medium">{user.name || 'N/A'}</p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Email</span>
              </div>
              <p className="text-gray-900 font-medium">{user.email}</p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Role</span>
              </div>
              <span className={`badge ${getStatusBadgeClass(user.role)}`}>
                {capitalize(user.role)}
              </span>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Status</span>
              </div>
              <span className={`badge ${getStatusBadgeClass(user.isActive ? 'active' : 'inactive')}`}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Package className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Plan</span>
              </div>
              <span className={`text-sm font-medium ${getPlanColor(user.plan)}`}>
                {capitalize(user.plan)}
              </span>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Member Since</span>
              </div>
              <p className="text-gray-900 font-medium">{formatDate(user.createdAt)}</p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Usage</span>
              </div>
              <p className="text-gray-900 font-medium">
                {formatNumber(user.usageCount || 0)} / {user.usageLimit == '-1' ? '∞' : formatNumber(user.usageLimit || 0)}
              </p>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Last Updated</span>
              </div>
              <p className="text-gray-900 font-medium">{formatDate(user.updatedAt)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue & Subscription Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-xl font-bold text-gray-900">
                  ${formatNumber(totalRecurringRevenue)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {subscription.billingCycle === 'monthly' ? 'per month' : subscription.billingCycle === 'yearly' ? 'per year' : 'one-time'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">MRR</p>
                <p className="text-xl font-bold text-gray-900">
                  ${formatNumber(monthlyRecurringRevenue)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Monthly Recurring</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">ARR</p>
                <p className="text-xl font-bold text-gray-900">
                  ${formatNumber(yearlyRecurringRevenue)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Annual Recurring</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Renewals</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.totalRenewals || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Lifetime renewals</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Details */}
      {user.subscription && (
        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Status</span>
                </div>
                <span className={`badge ${getStatusBadgeClass(subscription.status)}`}>
                  {capitalize(subscription.status || 'none')}
                </span>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Billing Cycle</span>
                </div>
                <p className="text-gray-900 font-medium">
                  {capitalize(subscription.billingCycle || 'N/A')}
                </p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Start Date</span>
                </div>
                <p className="text-gray-900 font-medium">
                  {subscription.startDate ? formatDate(subscription.startDate) : 'N/A'}
                </p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">End Date</span>
                </div>
                <p className="text-gray-900 font-medium">
                  {subscription.endDate ? formatDate(subscription.endDate) : 'N/A'}
                </p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Next Billing</span>
                </div>
                <p className="text-gray-900 font-medium">
                  {subscription.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A'}
                </p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Last Payment</span>
                </div>
                <p className="text-gray-900 font-medium">
                  {subscription.lastPaymentDate ? formatDate(subscription.lastPaymentDate) : 'N/A'}
                </p>
              </div>
              {subscription.stripeCustomerId && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">Stripe Customer ID</span>
                  </div>
                  <p className="text-gray-900 font-mono text-xs">
                    {subscription.stripeCustomerId}
                  </p>
                </div>
              )}
              {subscription.stripeSubscriptionId && (
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">Stripe Subscription ID</span>
                  </div>
                  <p className="text-gray-900 font-mono text-xs">
                    {subscription.stripeSubscriptionId}
                  </p>
                </div>
              )}
              {subscription.cancelAtPeriodEnd && (
                <div className="col-span-full">
                  <div className="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">
                      Subscription will be cancelled at period end
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Transcripts</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.totalTranscripts || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.completedTranscripts || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Processing</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.processingTranscripts || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Failed</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.failedTranscripts || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {stats.recentTranscripts && stats.recentTranscripts.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentTranscripts.map((transcript) => (
                    <tr key={transcript._id}>
                      <td>
                        <div className="font-medium text-gray-900">
                          {transcript.title || 'Untitled'}
                        </div>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">
                          {capitalize(transcript.source || 'N/A')}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(transcript.status)}`}>
                          {capitalize(transcript.status)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-600">
                          {formatDate(transcript.createdAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
